<template>
  <Teleport to="body">
    <Transition name="loading-fade">
      <div
        v-if="modelValue"
        ref="rootEl"
        class="loading-box"
        :style="styleVars"
        aria-busy="true"
        role="status"
        tabindex="0"
        @keydown.capture.prevent
        @keyup.capture.prevent
        @keypress.capture.prevent
        @wheel.capture.prevent
        @touchmove.capture.prevent
      >
        <img :src="resolvedSrc" :alt="alt" />
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import {
  computed,
  watchEffect,
  watch,
  nextTick,
  ref,
  defineProps,
  onBeforeUnmount,
} from "vue";
import defaultGif from "@/assets/loading.gif";

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  src: { type: String, default: "" },
  alt: { type: String, default: "loading" },
  blur: { type: Number, default: 4 },
  dim: { type: Number, default: 0.55 },
  zIndex: { type: Number, default: 9999 },

  imgWidth: { type: String, default: "80%" },
  imgMaxWidth: { type: String, default: "288px" },

  lockScroll: { type: Boolean, default: true },

  // ✅ 추가: 전역 입력 차단(뚫림 방지)
  lockInput: { type: Boolean, default: true },
});

const rootEl = ref(null);
const resolvedSrc = computed(() => props.src || defaultGif);

const styleVars = computed(() => ({
  "--blur": `${props.blur}px`,
  "--dim": String(props.dim),
  "--z": String(props.zIndex),
  "--img-w": props.imgWidth,
  "--img-max": props.imgMaxWidth,
}));

// 스크롤 잠금
watchEffect(() => {
  if (!props.lockScroll) return;
  if (props.modelValue) {
    document.documentElement.style.overflow = "hidden";
    document.body.style.overflow = "hidden";
  } else {
    document.documentElement.style.overflow = "";
    document.body.style.overflow = "";
  }
});

// ✅ 로딩 켜질 때 오버레이로 포커스 이동 (키보드 경로 차단)
watch(
  () => props.modelValue,
  async (v) => {
    if (!v) return;
    await nextTick();
    rootEl.value?.focus?.();
  }
);

// ✅ 전역 입력 차단(캡처 단계에서 막기)
function block(e) {
  e.preventDefault();
  e.stopPropagation();
}

const addGlobalBlockers = () => {
  window.addEventListener("click", block, true);
  window.addEventListener("mousedown", block, true);
  window.addEventListener("mouseup", block, true);
  window.addEventListener("touchstart", block, true);
  window.addEventListener("touchend", block, true);
};

const removeGlobalBlockers = () => {
  window.removeEventListener("click", block, true);
  window.removeEventListener("mousedown", block, true);
  window.removeEventListener("mouseup", block, true);
  window.removeEventListener("touchstart", block, true);
  window.removeEventListener("touchend", block, true);
};

watchEffect(() => {
  if (!props.lockInput) return;

  if (props.modelValue) addGlobalBlockers();
  else removeGlobalBlockers();
});

// 언마운트 시 정리
onBeforeUnmount(() => {
  removeGlobalBlockers();
  document.documentElement.style.overflow = "";
  document.body.style.overflow = "";
});
</script>

<style scoped lang="scss">
.loading-box {
  position: fixed;
  inset: 0;
  z-index: var(--z);
  background: rgba(0, 0, 0, var(--dim));
  display: flex;
  justify-content: center;
  align-items: center;

  backdrop-filter: blur(var(--blur));
  -webkit-backdrop-filter: blur(var(--blur));

  pointer-events: all;
  touch-action: none;

  > img {
    width: var(--img-w);
    max-width: var(--img-max);
    height: auto;
    filter: drop-shadow(0 10px 24px rgba(0, 0, 0, 0.45));
  }
}

.loading-fade-enter-active,
.loading-fade-leave-active {
  transition: opacity 0.18s ease;
}
.loading-fade-enter-from,
.loading-fade-leave-to {
  opacity: 0;
}
</style>
